import { useEffect, useState } from "react";
import { equipmentApi } from "../../services/equipmentApi";
import { useToast } from "../../context/ToastContext";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { Card } from "../../components/common/Card";
import { PageHeader } from "../../components/common/PageHeader";
import { Button } from "../../components/common/Button";
import { Badge } from "../../components/common/Badge";
import { DataTable } from "../../components/common/DataTable";
import { Tabs } from "../../components/common/Tabs";
import { Modal } from "../../components/common/Modal";
import { MergeEquipmentDialog } from "../../components/business/MergeEquipmentDialog";
import { OptionListManager } from "../../components/common/OptionListManager";
import { LockedFeature } from "../../components/common/LockedFeature";
import { SelectOrAdd } from "../../components/common/SelectOrAdd";
import { Field } from "../../components/common/Field";


export function EquipmentPage() {
  const { notify } = useToast();
  const { hasFeature, canChangeLists } = useFeatureTheme();
  const [inventory, setInventory] = useState(null);
  const [reorder, setReorder] = useState(null);
  const [open, setOpen] = useState(false);
  // Two names for one piece of kit (#299). Rare enough to live behind a
  // button rather than on every row: merging is something somebody does
  // once, having noticed a duplicate.
  const [merging, setMerging] = useState(false);
  // Closed by default: see the card below.
  const [managingTypes, setManagingTypes] = useState(false);
  const [form, setForm] = useState({ item_type: "Frame", quantity: "", reorder_threshold: "", cost_per_unit: "" });
  // What is actually usable, per item (V074). Kept beside the inventory
  // rows rather than replacing them, because the two answer different
  // questions: what you bought, and what you can pick up today.
  const [stock, setStock] = useState([]);
  /* One row per item TYPE (#395). Rolled up server-side because the
     rollup makes a judgement -- the reorder threshold is the highest
     of the group, not the sum -- and two clients deciding that
     separately is how the phone and the desk warn at different points. */
  const [owned, setOwned] = useState([]);
  const [adjusting, setAdjusting] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ movement_type: "Purchase", quantity: "", reason: "" });

  /* Newest first, which the endpoint does not do -- it orders by
     item_type for the rollup's benefit. Sorted here rather than asking
     for a second ordering, because it is presentation. Copied rather
     than sorted in place: mutating the state array would reorder the
     rollup's source under it. */
  const purchases = [...(inventory || [])].sort(
    (a, b) => String(b.purchase_date || "").localeCompare(String(a.purchase_date || ""))
      || b.equipment_id - a.equipment_id,
  );

  const stockFor = (equipmentId) =>
    stock.find((s) => String(s.equipment_id) === String(equipmentId));

  async function submitAdjust() {
    const quantity = Number(adjustForm.quantity);
    if (!quantity || quantity < 1) return notify("How many?", "warning");
    try {
      await equipmentApi.adjust({
        equipment_id: adjusting.equipment_id,
        movement_type: adjustForm.movement_type,
        quantity,
        reason: adjustForm.reason || undefined,
      });
      notify("Recorded", "success");
      setAdjusting(null);
      setAdjustForm({ movement_type: "Purchase", quantity: "", reason: "" });
      load();
    } catch (err) {
      // The server refuses what would make the ledger describe something
      // impossible -- repairing more than are broken, losing more than
      // exist -- and its sentence names the numbers.
      notify(err.apiMessage || "Could not record that", "danger");
    }
  }

  async function load() {
    // allSettled: the stock figures are the point of this page, and a
    // reorder call that fails must not take them down with it.
    const [invRes, reorderRes, stockRes] = await Promise.allSettled([
      equipmentApi.list(), equipmentApi.reorder(), equipmentApi.stock(),
    ]);
    setInventory(invRes.status === "fulfilled" ? invRes.value.equipment : []);
    setReorder(reorderRes.status === "fulfilled" ? reorderRes.value.reorder_needed : []);
    setStock(stockRes.status === "fulfilled" ? stockRes.value.stock : []);
    setOwned(stockRes.status === "fulfilled" ? (stockRes.value.owned || []) : []);
  }
  useEffect(() => { load(); }, []);

  async function submit() {
    try {
      await equipmentApi.add({
        item_type: form.item_type,
        quantity: Number(form.quantity || 0),
        reorder_threshold: form.reorder_threshold ? Number(form.reorder_threshold) : undefined,
        cost_per_unit: form.cost_per_unit ? Number(form.cost_per_unit) : undefined,
      });
      notify("Equipment added", "success");
      setOpen(false);
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not add equipment", "danger");
    }
  }

  return (
    <div>
      <PageHeader title="Equipment Inventory">
        {/* SECONDARY, and FIRST -- which is to say leftmost. Only worth
            offering once there are two things to merge. Adding equipment
            is the everyday action, so it takes the rightmost place;
            merging is something somebody does having noticed a
            duplicate, and it sits beside it rather than past it. */}
        {(inventory || []).length > 1 && (
          <Button variant="secondary" onClick={() => setMerging(true)}>Merge Duplicates</Button>
        )}
        <Button onClick={() => setOpen(true)}>Add Equipment</Button>
      </PageHeader>

      <LockedFeature enabled={hasFeature("EquipmentManagement")} featureLabel="Equipment Management" previewText="Track boxes, frames, feeders, and reorder alerts across your apiary.">
        {reorder && reorder.length > 0 && (
          <Card title="⚠️ Reorder Alerts" className="section-spacing">
            <ul>
              {reorder.map((r) => (
                <li key={r.equipment_id}>
                  <Badge tone="warning">{r.item_type}</Badge> {r.quantity} left (threshold {r.reorder_threshold})
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* ── TWO LISTS, BECAUSE THERE ARE TWO QUESTIONS (#395) ──

            Reported from a device: "Added multiple suit purchases and
            they are at the bottom of the list with the newest purchased
            at the top of the 'suit' group instead of combining all the
            suit entries into a single batch of Suit."

            add_equipment INSERTs a row per purchase, so five suit
            purchases are five rows all called "Suit". One list was being
            asked both "what do I own" and "what did I buy, and when",
            and answered neither -- several Suit rows that do not total,
            ordered by a date nobody came for.

            The books were never wrong; #319's expenditures and the P&L
            track without double counting. Only the list was. */}
        <Card>
          <Tabs
            tabs={[
              {
                key: "own",
                label: "What you own",
                content: (
                  <DataTable
                    rows={owned}
                    loading={!inventory}
                    rowKey="item_type"
                    emptyTitle="No equipment tracked yet"
                    emptyDescription="Hive bodies, supers, frames and tools — what you own and what condition it is in."
                    columns={[
                      {
                        key: "item_type",
                        header: "Type",
                        render: (r) => (
                          <span>
                            {r.item_type}
                            {/* Says the row is a total without making
                                somebody open the other tab to find out. */}
                            {r.purchases > 1 && (
                              <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                                {` · ${r.purchases} purchases`}
                              </span>
                            )}
                          </span>
                        ),
                      },
                      // AVAILABLE first, because it is the only figure
                      // somebody acts on. "Quantity" sat here and meant
                      // how many you OWN, so the page reported ten frames
                      // while eight were nailed into hives (V074).
                      {
                        key: "available",
                        header: "Free to use",
                        render: (r) => (
                          <span>
                            <strong>{r.available}</strong>
                            <span style={{ color: "var(--color-text-muted)" }}> of {r.owned}</span>
                          </span>
                        ),
                      },
                      {
                        key: "elsewhere",
                        header: "Out / needs repair",
                        render: (r) => {
                          const parts = [];
                          if (r.out_on_hives) parts.push(`${r.out_on_hives} on hives`);
                          if (r.needs_repair) parts.push(`${r.needs_repair} needs repair`);
                          // Nothing rather than "0 / 0": a row with
                          // nothing to say should say nothing.
                          return parts.length ? parts.join(" · ") : "—";
                        },
                      },
                      {
                        key: "reorder_threshold",
                        header: "Reorder At",
                        // The HIGHEST of the group, not the sum -- see
                        // equipment_stock.owned_by_type. Blank when nobody
                        // set one, because 0 would promise a warning at
                        // empty rather than no warning.
                        render: (r) => (r.reorder_threshold ?? "—"),
                      },
                    ]}
                  />
                ),
              },
              {
                key: "bought",
                label: "What you bought",
                content: (
                  <DataTable
                    rows={purchases}
                    loading={!inventory}
                    rowKey="equipment_id"
                    emptyTitle="Nothing bought yet"
                    emptyDescription="Every purchase you record appears here, newest first, with what it cost."
                    columns={[
                      // NEWEST FIRST and date-led, because this is the
                      // question asked at a desk. The other tab is the
                      // one asked in a storeroom.
                      {
                        key: "purchase_date",
                        header: "Bought",
                        render: (r) => (r.purchase_date
                          ? new Date(r.purchase_date).toLocaleDateString()
                          : "—"),
                      },
                      { key: "item_type", header: "Type" },
                      { key: "quantity", header: "How many" },
                      {
                        key: "cost_per_unit",
                        header: "Cost/Unit",
                        render: (r) => (r.cost_per_unit ? `$${Number(r.cost_per_unit).toFixed(2)}` : "—"),
                      },
                      {
                        key: "adjust",
                        header: "",
                        render: (r) => (
                          <Button size="sm" variant="secondary" onClick={() => setAdjusting(r)}>
                            Adjust
                          </Button>
                        ),
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
        </Card>

        {/* THE OTHER HALF OF SelectOrAdd (#417).
            This page has been able to type a NEW equipment type since
            #299, and the endpoint that puts one away has existed just as
            long with nothing calling it -- so the list could only ever
            grow, and a typo lived in the dropdown forever.

            Behind Manage rather than open, the same way "By varietal"
            does it: tidying this list is a once-after-a-typo gesture, and
            an always-open list of every type repeats what the Type
            dropdown already shows. */}
        <Card
          title="Equipment types"
          className="section-spacing"
          actions={canChangeLists === false ? null : (
            <Button size="sm" variant="secondary" onClick={() => setManagingTypes((v) => !v)}>
              {managingTypes ? "Done" : "Manage"}
            </Button>
          )}
        >
          {managingTypes ? (
            <OptionListManager
              listKey="equipment_type"
              emptyText="You have not added any types of your own yet — these are all built in."
              // The Type dropdown on this page is holding the old list.
              onChanged={load}
            />
          ) : (
            <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
              Put away a type you no longer keep, or bring one back. What you
              already recorded under it keeps its name.
            </p>
          )}
        </Card>
      </LockedFeature>

      <MergeEquipmentDialog
        open={merging}
        inventory={inventory}
        stock={stock}
        onClose={() => setMerging(false)}
        onMerged={() => { setMerging(false); load(); }}
      />

      <Modal open={open} onClose={() => setOpen(false)} title="Add Equipment" footer={<Button onClick={submit}>Save</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <SelectOrAdd
            listKey="equipment_type"
            label="Type"
            value={form.item_type}
            onChange={(v) => setForm((f) => ({ ...f, item_type: v }))}
          />
          <Field label="Quantity">
            {(id) => <input id={id} type="number" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} className="touch-target" />}
          </Field>
          <Field label="Reorder threshold" hint="Warn me when stock drops below this">
            {(id) => <input id={id} type="number" value={form.reorder_threshold} onChange={(e) => setForm((f) => ({ ...f, reorder_threshold: e.target.value }))} className="touch-target" />}
          </Field>
          <Field label="Cost per unit">
            {(id) => <input id={id} type="number" value={form.cost_per_unit} onChange={(e) => setForm((f) => ({ ...f, cost_per_unit: e.target.value }))} className="touch-target" />}
          </Field>
        </div>
      </Modal>

      {/* Changing a count, and saying why (#287).
          One dialog for six kinds of event, because they are all "the
          number moved and here is the reason". The person types a
          POSITIVE number and picks what happened -- asking somebody to
          type -6 for a loss invites the wrong sign at the worst moment. */}
      <Modal
        open={Boolean(adjusting)}
        onClose={() => setAdjusting(null)}
        title={adjusting ? `Adjust ${adjusting.item_type}` : "Adjust"}
        footer={<Button onClick={submitAdjust}>Record</Button>}
      >
        {adjusting && (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            {(() => {
              const s = stockFor(adjusting.equipment_id);
              if (!s) return null;
              return (
                <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
                  {s.owned} owned · {s.out_on_hives} on hives · {s.needs_repair} needs repair ·{" "}
                  <strong>{s.available} free</strong>
                </p>
              );
            })()}

            <Field label="What happened?">
              {(id, aria) => (
                <select
                  id={id}
                  {...aria}
                  className="touch-target"
                  value={adjustForm.movement_type}
                  onChange={(e) => setAdjustForm((f) => ({ ...f, movement_type: e.target.value }))}
                >
                  <option value="Purchase">Bought more</option>
                  <option value="Loss">Lost or thrown away</option>
                  <option value="Damaged">Damaged — needs repair</option>
                  <option value="Repaired">Repaired — usable again</option>
                  <option value="Correction">Miscounted — correcting</option>
                </select>
              )}
            </Field>

            <Field label="How many?">
              {(id, aria) => (
                <input
                  id={id}
                  {...aria}
                  type="number"
                  min="1"
                  className="touch-target"
                  value={adjustForm.quantity}
                  onChange={(e) => setAdjustForm((f) => ({ ...f, quantity: e.target.value }))}
                />
              )}
            </Field>

            <Field
              label="Why (optional)"
              hint="Worth a line. It is the difference between a number somebody trusts and one they argue with."
            >
              {(id, aria) => (
                <input
                  id={id}
                  {...aria}
                  className="touch-target"
                  value={adjustForm.reason}
                  onChange={(e) => setAdjustForm((f) => ({ ...f, reason: e.target.value }))}
                  placeholder="e.g. wax moth in storage"
                />
              )}
            </Field>

            {/* Damaged does NOT reduce what you own, and saying so here
                stops somebody recording it twice -- once as damage and
                again as a loss. */}
            {adjustForm.movement_type === "Damaged" && (
              <p style={{ margin: 0, color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
                They stay yours — just not usable until repaired.
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
